import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/base/ui/dialog'
import {ScrollArea} from '@/base/ui/scrollArea'
import {cn} from '@/lib/utils'
import React, {type FC, type ReactNode} from 'react'

// Constants
const MAX_HEIGHT = '600px'
const MAX_WIDTH = {
  sm: '400px',
  md: '600px',
  lg: '1200px',
} as const

const BUTTON_VARIANTS = {
  primary: 'bg-blue-p text-white hover:bg-opacity-70',
  secondary: 'hover:bg-blue-p hover:bg-gray-100 text-blue-p',
  iconOnly:
    'p-2 hover:bg-gray-40 min-w-[auto] disabled:grayscale disabled:opacity-80 focus-visible:ring-offset-0 focus:bg-blue-soft',
} as const

// Types
type IModalSize = keyof typeof MAX_WIDTH
type IButtonVariant = keyof typeof BUTTON_VARIANTS

export interface IWuModalProps extends React.ComponentProps<typeof Dialog> {
  Trigger?: ReactNode
  children?: ReactNode
  size?: IModalSize
  hideCloseButton?: boolean
  preventClickOutside?: boolean
  onOpenChange?: (open: boolean) => void
  open?: boolean
  dir?: 'ltr' | 'rtl'
  maxWidth?: string
  maxHeight?: string
}

// Components
export const WuModal: FC<IWuModalProps> = ({
  Trigger,
  children,
  size = 'md',
  hideCloseButton,
  preventClickOutside,
  onOpenChange,
  open,
  dir,
  maxWidth,
  maxHeight,
  ...rest
}) => {
  return (
    <Dialog {...rest} modal onOpenChange={onOpenChange} open={open}>
      {Trigger && <DialogTrigger asChild>{Trigger}</DialogTrigger>}
      <DialogContent
        style={{
          width: 'calc(100% - 64px)',
          maxWidth: maxWidth || MAX_WIDTH[size],
          maxHeight: maxHeight || MAX_HEIGHT,
        }}
        hideCloseButton={hideCloseButton}
        onInteractOutside={e => preventClickOutside && e.preventDefault()}
        dir={dir}
        aria-describedby={undefined}
      >
        {children}
      </DialogContent>
    </Dialog>
  )
}

export const WuModalContent: FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  style = {},
  ...rest
}) => {
  return (
    <ScrollArea className="flex flex-col gap-4 px-3 py-4 rtl:text-right">
      <div
        {...rest}
        style={{
          ...style,
          paddingLeft: `${(Number(style?.paddingLeft) || 0) + 4}px`,
          paddingRight: `${(Number(style?.paddingRight) || 0) + 4}px`,
        }}
      >
        {children}
      </div>
    </ScrollArea>
  )
}

export const WuModalHeader: FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...rest
}) => (
  <DialogHeader {...rest} className={cn('text-lg font-medium', className)}>
    <DialogTitle className="text-gray-lead">{children}</DialogTitle>
  </DialogHeader>
)

export const WuModalFooter: FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...rest
}) => (
  <DialogFooter
    {...rest}
    className={cn('border-t border-gray-40 bg-white h-16', className)}
  >
    {children}
  </DialogFooter>
)

interface IModalCloseProps extends React.ComponentProps<typeof DialogClose> {
  children?: ReactNode
  variant?: IButtonVariant
}
export const WuModalClose: FC<IModalCloseProps> = ({
  children,
  variant = 'primary',
  className,
  ...rest
}) => (
  <DialogClose
    {...rest}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-normal ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-soft focus-visible:ring-offset-2 disabled:cursor-not-allowed leading-4 disabled:bg-gray-20 disabled:text-gray-subtle disabled:opacity-80 disabled:ring-gray-subtle px-6 py-2 min-w-[104px] h-8',
      BUTTON_VARIANTS[variant],
      className,
    )}
  >
    {children}
  </DialogClose>
)
